/**
 * PTEL Tool - Auth Types
 * Tipos TypeScript compartidos para componentes de autenticación
 * 
 * @module components/auth/auth.types
 * @version 1.1.0
 * @date 2025-12-26
 */

// ============================================================================
// THEME
// ============================================================================

export type Theme = 'dark' | 'light';

// ============================================================================
// FORM STATES
// ============================================================================

export interface FieldValidation {
  valid: boolean;
  message?: string;
}

export type TouchedFields<T extends string> = Record<T, boolean>;
export type FormErrors<T extends string> = Partial<Record<T, string>>;

// ============================================================================
// LOGIN
// ============================================================================

export interface LoginFormData {
  email: string;
  password: string;
}

export interface LoginFormProps {
  theme: Theme;
  onSubmit: (email: string, password: string) => Promise<void>;
  onResendVerification?: (email: string, password: string) => Promise<void>;
  onNavigateToRegister?: () => void;
  onNavigateToPasswordReset?: () => void;
  isLoading?: boolean;
  externalError?: string | null;
  onClearError?: () => void;
  onClose?: () => void;
}

// ============================================================================
// REGISTER
// ============================================================================

export interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface RegisterFormProps {
  theme: Theme;
  /** Callback con displayName (nombre completo), firstName y lastName separados */
  onSubmit: (
    email: string, 
    password: string, 
    displayName: string,
    firstName: string,
    lastName: string
  ) => Promise<void>;
  onNavigateToLogin?: () => void;
  isLoading?: boolean;
  externalError?: string | null;
  onClearError?: () => void;
  onClose?: () => void;
}

export interface PasswordStrength {
  score: number;
  label: string;
  colorKey: 'red' | 'orange' | 'yellow' | 'blue' | 'teal';
}

// ============================================================================
// PASSWORD RESET
// ============================================================================

export interface PasswordResetFormProps {
  theme: Theme;
  onSubmit: (email: string) => Promise<void>;
  onNavigateToLogin?: () => void;
  isLoading?: boolean;
  externalError?: string | null;
  onClearError?: () => void;
  onClose?: () => void;
  hideCard?: boolean;
}

// ============================================================================
// AUTH MODAL
// ============================================================================

export type AuthView = 'login' | 'register' | 'password-reset';

export interface AuthModalProps {
  theme: Theme;
  initialView?: AuthView;
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (
    email: string, 
    password: string, 
    displayName: string,
    firstName: string,
    lastName: string
  ) => Promise<void>;
  onPasswordReset: (email: string) => Promise<void>;
  onResendVerification?: (email: string, password: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
  onLoginSuccess?: () => void;
}

// ============================================================================
// VALIDATION RULES
// ============================================================================

export const VALIDATION_RULES = {
  firstName: {
    required: 'El nombre es obligatorio',
    minLength: 2,
    maxLength: 50,
    tooShort: 'El nombre debe tener al menos 2 caracteres',
    tooLong: 'El nombre no puede superar los 50 caracteres',
  },
  lastName: {
    required: 'Los apellidos son obligatorios',
    minLength: 2,
    maxLength: 100,
    tooShort: 'Los apellidos deben tener al menos 2 caracteres',
    tooLong: 'Los apellidos no pueden superar los 100 caracteres',
  },
  email: {
    required: 'El correo electrónico es obligatorio',
    invalid: 'Introduce un correo electrónico válido',
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  password: {
    required: 'La contraseña es obligatoria',
    minLength: 6,
    maxLength: 128,
    tooShort: 'La contraseña debe tener al menos 6 caracteres',
    tooLong: 'La contraseña no puede superar los 128 caracteres',
    weak: 'La contraseña debe incluir mayúsculas, minúsculas y números',
  },
  confirmPassword: {
    required: 'Confirma tu contraseña',
    mismatch: 'Las contraseñas no coinciden',
  },
} as const;
